import React, { useEffect, useMemo } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import useApi, { Status } from '../../common/hooks/useApi';
import TransactionApi from '../../services/transaction/TransactionService';
import TransactionList from './components/TransactionList';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import { Helmet } from 'react-helmet';

const TransactionsPage = (): JSX.Element => {
  const { t, i18n } = useTranslation(['familiplusPage', 'component']);
  const { user } = useAuth0();
  const [fetchTransactions, transactions, fetchingTransactionStatus] = useApi(TransactionApi.fetchMany);
  const [fetchTransactionTypes, transactionTypes, fetchingTransTypeStatus] = useApi(TransactionApi.fetchTypes);

  useEffect(() => {
    fetchTransactions(user?.session_id, { nbResult: 100 });
    fetchTransactionTypes();
  }, [fetchTransactions, fetchTransactionTypes, user?.session_id]);

  const isDataPending = fetchingTransactionStatus === Status.Pending || fetchingTransTypeStatus === Status.Pending;
  const isDataResolved = fetchingTransactionStatus === Status.Resolved && fetchingTransTypeStatus === Status.Resolved;
  const isDataRejected = fetchingTransactionStatus === Status.Rejected || fetchingTransTypeStatus === Status.Rejected;

  const transactionsByYear = useMemo(() => {
    const results = new Map();
    transactions?.map((transaction) => {
      const year = transaction.Date.substring(0, 4);
      if (!results.has(year)) {
        results.set(year, []);
      }
      results.get(year).push(transaction);
    });
    return results;
  }, [transactions]);

  return (
    <Box>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{t('MetaTag.TransactionTitle', { ns: 'component' })}</title>
        <meta name="description" content={t('MetaTag.TransactionDescription', { ns: 'component' }).toString()} />
      </Helmet>
      <Link
        component={RouterLink}
        to={`/${i18n.resolvedLanguage}/${i18n.resolvedLanguage == 'fr' ? 'Compte' : 'Account'}/Familiplus`}
        className="iconTextLink">
        <ArrowBack sx={{ mr: '6px' }} fontSize="small" />
        {t('transactions.backToSummary')}
      </Link>

      <Typography variant="h1" sx={{ mb: '12px', mt: '32px' }}>
        {t('transactions.allTitle')}
      </Typography>

      {isDataPending && <LoadingIndicator />}
      {isDataRejected && (
        <Alert id="transactionsFetchDataFailed" severity="error">
          {t('transactions.fetchDataFailed')}
        </Alert>
      )}
      {isDataResolved && (
        <>
          {transactions && transactions.length > 0 ? (
            <>
              {Array.from(transactionsByYear.keys()).map((year) => (
                <React.Fragment key={year}>
                  <Typography variant="body1" component="h2" sx={{ mb: '12px', mt: '36px', fontWeight: 'bold' }}>
                    {year}
                  </Typography>
                  <Box sx={{ mx: '-16px', mb: '16px' }}>
                    <TransactionList
                      transactions={transactionsByYear.get(year)}
                      transactionTypes={transactionTypes || []}
                    />
                  </Box>
                </React.Fragment>
              ))}
            </>
          ) : (
            <Box sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}>{t('transactions.noResult')}</Box>
          )}
        </>
      )}
    </Box>
  );
};

export default TransactionsPage;
